// Navbar internals split out for clarity — the file holds the
// MEGA-MENU rendering pieces (desktop ribbon, mobile picker, branded
// program icons). They were originally inline in Navbar.jsx; moving
// them here lets us keep Navbar.jsx focused on the chrome (logo,
// trigger buttons, mobile overlay shell, auth menu).
//
// Public exports:
//   • ProgramIcon          — branded ⚡ / 1 / 2 badge for the picker
//   • MegaRibbon           — full-width desktop slide-down panel
//   • MobileChallengePicker — full-screen mobile two-step picker
//
// All three components are stateless w.r.t. the outer Navbar — they
// receive a menu entry + onClose callback and render themselves.

import { useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronDown, ArrowUpRight, Zap, ArrowRight } from "lucide-react";
import { PLATFORMS } from "@/data/site";
import { prefetchProps } from "@/lib/prefetch";

/* Lookup map for platform meta (logo + brand accent) — used when a
   mega-menu item carries `platformId`, so platform links render with
   the real product logo and that product's brand colour instead of
   a generic Lucide icon. */
const PLATFORM_MAP = Object.fromEntries(PLATFORMS.map((p) => [p.id, p]));

/* Branded icon for the Challenges picker. Designed to visually match
   the rest of the mega-menu icon row (28×28 rounded-md tinted badge).
   The digits "1" and "2" are rendered via SVG so the y-coordinate is
   deterministic regardless of Outfit's font metrics. */
export function ProgramIcon({ kind, size = 28, className = "" }) {
  const badgeStyle = {
    width: size,
    height: size,
    background: "color-mix(in srgb, var(--brand) 8%, transparent)",
    border: "1px solid color-mix(in srgb, var(--brand) 28%, transparent)",
  };

  if (kind === "bolt") {
    // Instant program's lightning is amber/gold — visually distinct
    // from all the other speed-related ⚡ icons on the site (which
    // stay brand cyan). Communicates "instant FUNDING" as its own
    // identity rather than just "fast".
    return (
      <span
        aria-hidden
        className={`inline-grid place-items-center rounded-md shrink-0 ${className}`}
        style={{
          width: size,
          height: size,
          background: "color-mix(in srgb, #f59e0b 12%, transparent)",
          border: "1px solid color-mix(in srgb, #f59e0b 32%, transparent)",
        }}
      >
        <Zap size={Math.round(size * 0.5)} strokeWidth={2.4} fill="#f59e0b" style={{ color: "#f59e0b" }} />
      </span>
    );
  }
  const digit = kind === "one" ? "1" : "2";
  return (
    <span
      aria-hidden
      className={`inline-grid place-items-center rounded-md shrink-0 ${className}`}
      style={badgeStyle}
    >
      <svg width={size} height={size} viewBox="0 0 24 24" className="block" aria-hidden>
        <text
          x="12"
          y="12"
          textAnchor="middle"
          dominantBaseline="central"
          fontFamily="Outfit, system-ui, sans-serif"
          fontSize="15"
          fontWeight="700"
          fill="currentColor"
          className="text-brand"
          dy="0.6"
        >
          {digit}
        </text>
      </svg>
    </span>
  );
}

/* Two-step Challenge picker — left rail with 3 program TYPE cards,
   right pane reveals size pills (with price) for the active program.
   Default-active is the first program but user can hover/click to swap.
   Selecting a size routes to /?p=<id>&s=<amount>#challenges, which the
   ChallengesSection picks up via useSearchParams. */
function ChallengePicker({ entry, onClose }) {
  const { t } = useTranslation();
  const programs = entry.programs || [];
  const [active, setActive] = useState(programs[0]?.id);
  const activeProgram = programs.find((p) => p.id === active) || programs[0];
  const spotlight = entry.spotlight;

  return (
    <div className="max-w-7xl mx-auto px-5 lg:px-8 py-6">
      <div className="grid gap-6" style={{ gridTemplateColumns: "minmax(0,300px) 1fr 280px" }}>
        {/* LEFT — Step 1: Pick a challenge type */}
        <div className="min-w-0">
          <div className="text-[10px] uppercase tracking-[0.22em] text-[color:var(--text-3)] font-semibold mb-3 pl-1">
            {t("mega.step1", "Step 1 — Pick a challenge")}
          </div>
          <div className="flex flex-col gap-0.5" data-testid="challenges-type-rail">
            {programs.map((p) => {
              const isActive = p.id === active;
              return (
                <button
                  key={p.id}
                  type="button"
                  onMouseEnter={() => setActive(p.id)}
                  onFocus={() => setActive(p.id)}
                  onClick={() => setActive(p.id)}
                  data-testid={`challenges-type-${p.id}`}
                  aria-pressed={isActive}
                  className={`group flex items-center gap-2.5 px-2 py-1.5 min-h-[44px] rounded-lg transition-colors text-left ${
                    isActive ? "bg-[color:var(--surface-2)]" : "hover:bg-[color:var(--surface-2)]"
                  }`}
                >
                  <span className="shrink-0">
                    <ProgramIcon kind={p.iconKind} size={28} />
                  </span>
                  <span className="min-w-0 flex-1">
                    <span
                      className={`block text-[13px] font-medium truncate leading-tight transition-colors ${
                        isActive ? "text-brand" : "text-[color:var(--text-1)] group-hover:text-brand"
                      }`}
                    >
                      {p.label}
                    </span>
                    <span className="block text-[12px] text-[color:var(--text-3)] truncate font-light leading-tight">
                      {p.blurb}
                    </span>
                  </span>
                  <ChevronDown
                    size={12}
                    className={`-rotate-90 shrink-0 transition-opacity ${
                      isActive ? "opacity-100 text-brand" : "opacity-0 group-hover:opacity-60 text-[color:var(--text-3)]"
                    }`}
                  />
                </button>
              );
            })}
          </div>
        </div>

        {/* MIDDLE — Step 2: Pick an account size for the chosen type */}
        <div className="min-w-0 border-l border-[color:var(--line)] pl-6">
          <div className="text-[10px] uppercase tracking-[0.22em] text-[color:var(--text-3)] font-semibold mb-3 flex items-center gap-2">
            <span>{t("mega.step2", "Step 2 — Account size")}</span>
            <span className="text-brand normal-case tracking-normal font-medium inline-flex items-center gap-1.5">
              <ProgramIcon kind={activeProgram?.iconKind} size={16} />
              {activeProgram?.label}
            </span>
          </div>
          <AnimatePresence mode="wait">
            <motion.div
              key={activeProgram?.id}
              initial={{ opacity: 0, x: 12 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -12 }}
              transition={{ duration: 0.18, ease: "easeOut" }}
              className="grid grid-cols-2 gap-x-4 gap-y-0.5"
              data-testid={`challenges-sizes-${activeProgram?.id}`}
            >
              {(activeProgram?.sizes || []).map((s) => (
                <Link
                  key={s.label}
                  to={s.to}
                  onClick={onClose}
                  {...prefetchProps(s.to.split("?")[0])}
                  data-testid={`mega-size-${activeProgram?.id}-${s.label.replace("$","").toLowerCase()}`}
                  className="group flex items-center justify-between gap-2 rounded-lg px-2 py-1.5 min-h-[44px] hover:bg-[color:var(--surface-2)] transition-colors"
                >
                  <span className="min-w-0">
                    <span className="block font-display text-[14px] font-medium text-[color:var(--text-1)] leading-tight tabular-nums group-hover:text-brand transition-colors">
                      {s.label}
                    </span>
                    <span className="block text-[10px] uppercase tracking-[0.18em] text-[color:var(--text-3)] leading-tight mt-0.5">
                      {s.tier}
                    </span>
                  </span>
                  <span className="inline-flex items-center gap-1 shrink-0">
                    <span className="font-display text-[13px] font-semibold text-brand tabular-nums">{s.price}</span>
                    <ArrowRight size={12} className="text-brand opacity-50 group-hover:opacity-100 group-hover:translate-x-0.5 transition-all" />
                  </span>
                </Link>
              ))}
            </motion.div>
          </AnimatePresence>
        </div>

        {/* RIGHT — Spotlight CTA */}
        {spotlight && (
          <Link
            to={spotlight.cta.to}
            onClick={onClose}
            data-testid={`mega-spotlight-${entry.key}`}
            className="group relative overflow-hidden rounded-xl p-4 flex flex-col justify-between min-h-[180px] transition-transform"
            style={{
              background:
                "linear-gradient(135deg, color-mix(in srgb, var(--brand) 14%, var(--surface-2)) 0%, var(--surface) 60%, color-mix(in srgb, var(--brand-deep) 18%, var(--surface-2)) 100%)",
              border: "1px solid color-mix(in srgb, var(--brand) 32%, transparent)",
            }}
          >
            <div>
              <div className="text-[10px] uppercase tracking-[0.22em] text-brand font-semibold mb-1.5">
                {spotlight.eyebrow}
              </div>
              <div className="font-display text-[16px] font-semibold text-[color:var(--text-1)] leading-tight">
                {spotlight.title}
              </div>
              <p className="text-[11px] text-[color:var(--text-3)] mt-2 font-light leading-relaxed">
                {spotlight.body}
              </p>
            </div>
            <div className="inline-flex items-center gap-1.5 text-brand text-[12px] font-medium mt-3">
              {spotlight.cta.label}
              <ArrowUpRight size={13} className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
            </div>
          </Link>
        )}
      </div>
    </div>
  );
}

/* Full-width slide-down mega-menu ribbon. Anchored to the navbar's
   bottom edge so it always spans the full viewport width and cannot
   drift off-centre. Two layouts:
     • Challenges → uses ChallengePicker (two-step picker)
     • Everything else → column grid of links + right-side spotlight tile */
export function MegaRibbon({ entry, onClose }) {
  if (entry.picker === "challenges") {
    return (
      <motion.div
        key={entry.key}
        initial={{ opacity: 0, y: -8 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -8 }}
        transition={{ duration: 0.18, ease: "easeOut" }}
        className="absolute inset-x-0 top-full"
        data-testid={`mega-ribbon-${entry.key}`}
      >
        <div
          className="border-t border-b shadow-2xl"
          style={{
            background: "color-mix(in srgb, var(--surface) 97%, transparent)",
            backdropFilter: "blur(24px)",
            borderTopColor: "color-mix(in srgb, var(--brand) 18%, transparent)",
            borderBottomColor: "color-mix(in srgb, var(--brand) 12%, transparent)",
          }}
        >
          <ChallengePicker entry={entry} onClose={onClose} />
        </div>
      </motion.div>
    );
  }

  const spotlight = entry.spotlight;
  const SpotIcon = spotlight?.icon;
  const linkColCount = entry.columns.length;
  const gridCols = spotlight
    ? `repeat(${linkColCount}, minmax(0, 1fr)) 320px`
    : `repeat(${linkColCount}, minmax(0, 1fr))`;
  return (
    <motion.div
      key={entry.key}
      initial={{ opacity: 0, y: -8 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -8 }}
      transition={{ duration: 0.18, ease: "easeOut" }}
      className="absolute inset-x-0 top-full"
      data-testid={`mega-ribbon-${entry.key}`}
    >
      <div
        className="border-t border-b shadow-2xl"
        style={{
          background: "color-mix(in srgb, var(--surface) 97%, transparent)",
          backdropFilter: "blur(24px)",
          borderTopColor: "color-mix(in srgb, var(--brand) 18%, transparent)",
          borderBottomColor: "color-mix(in srgb, var(--brand) 12%, transparent)",
        }}
      >
        <div className="max-w-7xl mx-auto px-5 lg:px-8 py-5">
          <div className="grid gap-x-8 gap-y-4 items-start" style={{ gridTemplateColumns: gridCols }}>
            {entry.columns.map((col) => (
              <div key={col.heading} className="min-w-0">
                <div className="text-[10px] uppercase tracking-[0.22em] text-[color:var(--text-3)] font-semibold mb-2 pl-1">
                  {col.heading}
                </div>
                <div className="flex flex-col gap-0.5">
                  {col.items.map((it) => {
                    const plat = it.platformId ? PLATFORM_MAP[it.platformId] : null;
                    return (
                      <Link
                        key={it.label}
                        to={it.to}
                        data-testid={`mega-link-${it.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                        {...prefetchProps(it.to.split("?")[0])}
                        onClick={onClose}
                        className="group flex items-center gap-2.5 px-2 py-1.5 rounded-lg hover:bg-[color:var(--surface-2)] transition-colors"
                      >
                        <span
                          className="w-7 h-7 rounded-md grid place-items-center shrink-0 transition-colors overflow-hidden"
                          style={
                            plat
                              ? {
                                  background: `color-mix(in srgb, ${plat.accent} 14%, rgba(0,0,0,0.4))`,
                                  border: `1px solid color-mix(in srgb, ${plat.accent} 50%, transparent)`,
                                }
                              : {
                                  background: "color-mix(in srgb, var(--brand) 8%, transparent)",
                                  border: "1px solid color-mix(in srgb, var(--brand) 22%, transparent)",
                                }
                          }
                        >
                          {plat ? (
                            <img src={plat.logo} alt={plat.name} className="w-full h-full object-contain p-0.5" loading="lazy" />
                          ) : (
                            <it.Icon size={13} className="text-brand" />
                          )}
                        </span>
                        <span className="min-w-0 flex-1">
                          <span
                            className="block text-[13px] font-medium text-[color:var(--text-1)] truncate leading-tight transition-colors"
                            style={plat ? { "--hover-color": plat.accent } : undefined}
                          >
                            {it.label}
                          </span>
                          <span className="block text-[12px] text-[color:var(--text-3)] truncate font-light leading-tight">
                            {it.tagline}
                          </span>
                        </span>
                      </Link>
                    );
                  })}
                </div>
              </div>
            ))}

            {spotlight && (
              <Link
                to={spotlight.cta.to}
                onClick={onClose}
                data-testid={`mega-spotlight-${entry.key}`}
                className="group relative overflow-hidden rounded-xl p-4 flex flex-col justify-between min-h-[170px] transition-transform"
                style={{
                  background:
                    "linear-gradient(135deg, color-mix(in srgb, var(--brand) 14%, var(--surface-2)) 0%, var(--surface) 60%, color-mix(in srgb, var(--brand-deep) 18%, var(--surface-2)) 100%)",
                  border: "1px solid color-mix(in srgb, var(--brand) 32%, transparent)",
                  boxShadow: "inset 0 1px 0 color-mix(in srgb, var(--brand) 18%, transparent), 0 8px 24px rgba(0,0,0,0.25)",
                }}
              >
                {/* Decorative scan-grid */}
                <div
                  aria-hidden
                  className="absolute inset-0 opacity-[0.18] pointer-events-none"
                  style={{
                    backgroundImage:
                      "linear-gradient(0deg, transparent 95%, color-mix(in srgb, var(--brand) 50%, transparent) 100%), linear-gradient(90deg, transparent 95%, color-mix(in srgb, var(--brand) 30%, transparent) 100%)",
                    backgroundSize: "22px 22px",
                  }}
                />
                {/* Brand glow blob */}
                <div
                  aria-hidden
                  className="absolute -top-10 -right-10 w-32 h-32 rounded-full pointer-events-none"
                  style={{
                    background: "radial-gradient(circle, color-mix(in srgb, var(--brand) 28%, transparent) 0%, transparent 70%)",
                  }}
                />
                <div className="relative">
                  <div className="flex items-center gap-2 mb-2">
                    {SpotIcon && (
                      <span
                        className="w-7 h-7 rounded-md grid place-items-center"
                        style={{
                          background: "color-mix(in srgb, var(--brand) 18%, transparent)",
                          border: "1px solid color-mix(in srgb, var(--brand) 36%, transparent)",
                        }}
                      >
                        <SpotIcon size={13} className="text-brand" />
                      </span>
                    )}
                    <span className="text-[10px] uppercase tracking-[0.22em] text-brand font-semibold">{spotlight.eyebrow}</span>
                  </div>
                  <div className="font-display text-[15px] font-medium text-[color:var(--text-1)] leading-snug mb-1.5">
                    {spotlight.title}
                  </div>
                  <div className="text-[11.5px] text-[color:var(--text-2)] font-light leading-snug">{spotlight.body}</div>
                </div>

                {/* Platform logos row for the platforms spotlight */}
                {spotlight.kind === "platforms" && spotlight.platforms && (
                  <div className="relative mt-3 flex items-center gap-1.5">
                    {spotlight.platforms.map((p) => (
                      <span
                        key={p.id}
                        className="w-9 h-9 rounded-md grid place-items-center overflow-hidden p-1"
                        style={{
                          background: "rgba(0,0,0,0.55)",
                          border: "1px solid color-mix(in srgb, var(--brand) 24%, transparent)",
                        }}
                      >
                        <img src={p.logo} alt="" aria-hidden="true" className="w-full h-full object-contain" />
                      </span>
                    ))}
                  </div>
                )}

                <div className="relative mt-3 inline-flex items-center gap-1.5 text-[12px] font-medium text-brand group-hover:gap-2 transition-all">
                  {spotlight.cta.label}
                  <ArrowUpRight size={13} className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </div>
              </Link>
            )}
          </div>
        </div>
      </div>
    </motion.div>
  );
}

/* Mobile counterpart to ChallengePicker — type pills at top, sizes
   grid below for the chosen program. Keeps the same step-1-then-step-2
   flow inside the full-screen mobile overlay. */
export function MobileChallengePicker({ entry, onClose }) {
  const { t } = useTranslation();
  const programs = entry.programs || [];
  const [active, setActive] = useState(programs[0]?.id);
  const activeProgram = programs.find((p) => p.id === active) || programs[0];

  return (
    <div data-testid="mobile-challenge-picker">
      <div className="text-[10px] uppercase tracking-[0.22em] text-[color:var(--text-3)] font-semibold mb-2 px-1">
        {t("mega.step1", "Step 1 — Pick a challenge")}
      </div>
      <div className="grid grid-cols-3 gap-2 mb-4" data-testid="mobile-type-rail">
        {programs.map((p) => {
          const isActive = p.id === active;
          return (
            <button
              key={p.id}
              type="button"
              onClick={() => setActive(p.id)}
              data-testid={`mobile-challenges-type-${p.id}`}
              aria-pressed={isActive}
              className="flex flex-col items-center justify-center gap-1.5 py-3 rounded-xl transition-all active:scale-[0.97]"
              style={{
                background: isActive
                  ? "color-mix(in srgb, var(--brand) 14%, var(--surface-2))"
                  : "color-mix(in srgb, var(--brand) 4%, var(--surface-2))",
                border: isActive
                  ? "1px solid color-mix(in srgb, var(--brand) 55%, transparent)"
                  : "1px solid color-mix(in srgb, var(--brand) 14%, var(--line))",
              }}
            >
              <ProgramIcon kind={p.iconKind} size={24} />
              <span className="text-[11px] font-medium text-[color:var(--text-1)] leading-tight">
                {p.id === "instant" ? "Instant" : p.id === "oneStep" ? "1-Step" : "2-Step"}
              </span>
            </button>
          );
        })}
      </div>

      <div className="text-[10px] uppercase tracking-[0.22em] text-[color:var(--text-3)] font-semibold mb-2 px-1 flex items-center gap-1.5">
        <span>{t("mega.step2", "Step 2 — Account size")}</span>
        <span className="text-brand normal-case tracking-normal font-medium">· {activeProgram?.label}</span>
      </div>
      <AnimatePresence mode="wait">
        <motion.div
          key={activeProgram?.id}
          initial={{ opacity: 0, y: 6 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -6 }}
          transition={{ duration: 0.18 }}
          className="grid grid-cols-2 gap-2"
          data-testid={`mobile-sizes-${activeProgram?.id}`}
        >
          {(activeProgram?.sizes || []).map((s) => (
            <Link
              key={s.label}
              to={s.to}
              onClick={onClose}
              {...prefetchProps(s.to.split("?")[0])}
              data-testid={`mobile-size-${activeProgram?.id}-${s.label.replace("$","").toLowerCase()}`}
              className="flex items-center justify-between gap-2 px-3 py-3 rounded-xl active:scale-[0.97] transition-transform"
              style={{
                background: "color-mix(in srgb, var(--brand) 5%, var(--surface-2))",
                border: "1px solid color-mix(in srgb, var(--brand) 16%, transparent)",
              }}
            >
              <span className="min-w-0">
                <span className="block font-display text-[14px] font-medium text-[color:var(--text-1)] leading-tight tabular-nums">
                  {s.label}
                </span>
                <span className="block text-[10px] uppercase tracking-[0.18em] text-[color:var(--text-3)] leading-tight mt-0.5">
                  {s.tier}
                </span>
              </span>
              <span className="font-display text-[12px] font-semibold text-brand tabular-nums">{s.price}</span>
            </Link>
          ))}
        </motion.div>
      </AnimatePresence>
    </div>
  );
}

/* PlatformLink helper used by mobile menu items that carry `platformId` —
   exported so MobileMenu (in Navbar.jsx) can render platform tiles with
   the brand-correct logo and accent border. */
export { PLATFORM_MAP };
